/**
 * ContinueLastConversation — a single-row "Pick up where you left off" card
 * shown on Command Center. Fetches the most-recent conversation across all
 * 4 sources (AI Studio, CFO Buck, Quick Chat, Tax Plan) and deep-links to it.
 *
 * Hidden entirely when the user has zero conversations.
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../fetchInterceptor';
import {
  ArrowRight, Clock, Sparkles, BrainCircuit, MessageCircle, Calculator,
} from 'lucide-react';
import { cn } from '../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;

const SOURCE_META = {
  ai_studio: { label: 'AI Studio',  icon: Sparkles,     gradient: 'from-emerald-500 to-teal-500' },
  cfo_buck:  { label: 'CFO Buck',   icon: BrainCircuit, gradient: 'from-violet-500 to-fuchsia-500' },
  quick_chat:{ label: 'Quick Chat', icon: MessageCircle,gradient: 'from-amber-500 to-orange-500' },
  tax_plan:  { label: 'Tax Plan',   icon: Calculator,   gradient: 'from-blue-500 to-indigo-500' },
};

function timeAgo(iso) {
  if (!iso) return '';
  const diff = (new Date() - new Date(iso)) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function ContinueLastConversation({ compact = false, onPick }) {
  const navigate = useNavigate();
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchLatest = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API}/api/conversations/all?limit=1`);
      if (res.ok) {
        const data = await res.json();
        setLatest(data.conversations?.[0] || null);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchLatest(); }, [fetchLatest]);

  if (loading || !latest) return null;

  const meta = SOURCE_META[latest.source] || SOURCE_META.ai_studio;
  const Icon = meta.icon;

  // Click handler: in compact mode we prefer `onPick` (lets the parent open
  // the thread inline without navigating) and only fall back to deep_link
  // navigation if `onPick` isn't provided or the source isn't AI Studio.
  const handleClick = () => {
    if (compact && onPick && latest.source === 'ai_studio' && latest.id) {
      onPick(latest.id);
      return;
    }
    navigate(latest.deep_link);
  };

  if (compact) {
    return (
      <button
        type="button"
        onClick={handleClick}
        className="group w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-emerald-500/30 bg-gradient-to-r from-emerald-50/60 to-emerald-50/10 dark:from-emerald-950/30 dark:to-emerald-950/5 hover:border-emerald-500/60 transition-colors text-left mb-2"
        data-testid="resume-last-chat-pin"
      >
        <div className={cn(
          'w-7 h-7 rounded-md flex items-center justify-center text-white flex-shrink-0 bg-gradient-to-br',
          meta.gradient,
        )}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[9px] font-mono uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-400">
            Resume · {meta.label}
          </p>
          <p className="text-xs font-medium text-zinc-800 dark:text-zinc-200 truncate">
            {latest.title}
          </p>
        </div>
        <ArrowRight className="w-3.5 h-3.5 text-emerald-500 group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className="group w-full flex items-center gap-3 rounded-xl border border-emerald-500/30 bg-gradient-to-r from-emerald-50/40 via-white to-white dark:from-emerald-950/30 dark:via-zinc-900/40 dark:to-zinc-900/40 hover:border-emerald-500/60 hover:from-emerald-50 dark:hover:from-emerald-950/50 transition-all p-4 text-left"
      data-testid="continue-last-conversation"
    >
      <div className={cn(
        'w-10 h-10 rounded-lg flex items-center justify-center text-white flex-shrink-0 bg-gradient-to-br shadow-md',
        meta.gradient,
      )}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-emerald-700 dark:text-emerald-400">
          Pick up where you left off · {meta.label}
        </p>
        <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 truncate mt-0.5">
          {latest.title}
        </p>
        <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
          <Clock className="w-3 h-3" />
          {timeAgo(latest.updated_at)}
          {latest.message_count != null && <span>· {latest.message_count} message{latest.message_count === 1 ? '' : 's'}</span>}
        </p>
      </div>
      <ArrowRight className="w-5 h-5 text-emerald-500 group-hover:translate-x-1 transition-transform flex-shrink-0" />
    </button>
  );
}
